import React, { useEffect, useState, useCallback } from "react";
import { Button, Result, Card, Row, Col, Spin, Modal, Typography, Space, message, Input, Popconfirm, Tabs } from "antd";
import { PlusOutlined, EyeOutlined, CopyOutlined, DeleteOutlined, LoginOutlined, BankOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import { useAuth0 } from "../../../react-auth0-spa";
import ArubaCrdentialsModal from "../../shared/modals/ArubaCrdentialsModal";

const { Text } = Typography;

// GraphQL query to fetch companies for an org
const companiesQuery = `
query GetOrgCompanies($orgId: ID!) {
  companies(
    where: {
      org_pointer: {
        have: { objectId: { equalTo: $orgId } }
      }
    },
    order: [nome_ASC]
  ) {
    edges {
      node {
        objectId
        nome
        partita_iva
        enabled
      }
    }
  }
}`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const CredentialPanel = ({ orgId, companyId, companyLabel, getTokenSilently, isProd }) => {
  const [loading, setLoading] = useState(false);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalResult, setModalResult] = useState();
  const [viewCredentialsVisible, setViewCredentialsVisible] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [fetchingCredentials, setFetchingCredentials] = useState(false);
  const [deleteResult, setDeleteResult] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingCredentials, setDeletingCredentials] = useState(false);

  const baseUrl = config.fatturazioneElettronicaV3[isProd ? "prod" : "dev"];

  const buildQueryString = (params) => {
    const qs = new URLSearchParams(params);
    if (companyId) qs.set("companyId", companyId);
    return qs.toString();
  };

  const onResultAcked = () => {
    setModalVisible(false);
    setModalResult("");
  };

  const onCreateCredentials = (values) => {
    saveCredentials(values)
      .then(() => {
        setModalResult("OK");
        checkStatus();
      })
      .catch((error) => {
        console.error(error);
        setModalResult("ERROR");
      });
  };

  const saveCredentials = async (values) => {
    const token = await getTokenSilently();
    const payload = {
      ...values,
      orgId,
      ...(companyId && { companyId }),
    };
    const response = await fetch(`${baseUrl}/admin/credentials`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    return response.json();
  };

  const fetchCredentials = async () => {
    try {
      setFetchingCredentials(true);
      const token = await getTokenSilently();
      const qs = buildQueryString({ orgId, returnCredentials: "true" });
      const response = await fetch(`${baseUrl}/admin/credentials?${qs}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const responseData = await response.json();
      setCredentials({
        username: responseData.credentials.arubaUsername,
        password: responseData.credentials.arubaPassword,
      });
      setViewCredentialsVisible(true);
    } catch (error) {
      console.error(error);
      message.error("Errore nel recupero delle credenziali");
    } finally {
      setFetchingCredentials(false);
    }
  };

  const openArubaLoginWithCredentials = () => {
    try {
      window.open("https://fatturazioneelettronica.aruba.it/#login", "_blank", "noopener,noreferrer");
      fetchCredentials();
      message.info("Aperto il login Aruba in una nuova scheda. Copia le credenziali dal popup.");
    } catch (error) {
      console.error(error);
      message.error("Impossibile aprire il login Aruba");
    }
  };

  const deleteCredentials = async () => {
    try {
      setDeletingCredentials(true);
      const token = await getTokenSilently();
      const qs = buildQueryString({ orgId });
      const response = await fetch(`${baseUrl}/admin/credentials?${qs}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setDeleteResult("OK");
      } else {
        setDeleteResult("ERROR");
      }
      setDeleteModalVisible(true);
      checkStatus();
    } catch (error) {
      console.error(error);
      setDeleteResult("ERROR");
      setDeleteModalVisible(true);
    } finally {
      setDeletingCredentials(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => message.success("Copiato negli appunti"))
      .catch(() => message.error("Errore durante la copia"));
  };

  const checkStatus = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const qs = buildQueryString({ orgId });
      const response = await fetch(`${baseUrl}/admin/credentials?${qs}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const responseData = await response.json();
      setHasCredentials(responseData.orgHasCredentials);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTokenSilently, isProd, orgId, companyId]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  if (loading) {
    return <Spin />;
  }

  return (
    <Card style={{ textAlign: "center" }}>
      {companyLabel && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {companyLabel}
        </Text>
      )}
      {hasCredentials && <Result status="success" title="Credenziali Attive" />}
      {!hasCredentials && <Result status="warning" title="Credenziali Mancanti" />}
      <Button
        style={{ marginTop: "15px", marginBottom: hasCredentials ? "15px" : "0" }}
        type="primary"
        size="large"
        onClick={() => setModalVisible(true)}
      >
        Imposta Nuove Credenziali <PlusOutlined />
      </Button>
      {hasCredentials && (
        <>
          <Button
            style={{ marginBottom: "15px" }}
            type="primary"
            ghost
            size="large"
            onClick={openArubaLoginWithCredentials}
            loading={fetchingCredentials}
          >
            Accedi ad Aruba <LoginOutlined />
          </Button>
          <Button
            style={{ marginBottom: "15px" }}
            type="default"
            size="large"
            onClick={fetchCredentials}
            loading={fetchingCredentials}
          >
            Visualizza Credenziali <EyeOutlined />
          </Button>
          <Popconfirm
            title="Eliminare le credenziali?"
            description="Sei sicuro di voler eliminare le credenziali?"
            onConfirm={deleteCredentials}
            okText="Si"
            cancelText="No"
          >
            <Button
              style={{ marginBottom: "15px" }}
              type="primary"
              danger
              size="large"
              loading={deletingCredentials}
            >
              Elimina Credenziali <DeleteOutlined />
            </Button>
          </Popconfirm>
        </>
      )}
      <ArubaCrdentialsModal
        onResultAcked={onResultAcked}
        arubaModalResult={modalResult}
        visible={modalVisible}
        onCreate={onCreateCredentials}
        onCancel={() => setModalVisible(false)}
      />
      <Modal
        title="Credenziali Aruba"
        open={viewCredentialsVisible}
        onCancel={() => setViewCredentialsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewCredentialsVisible(false)}>
            Chiudi
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>Username:</Text>
            <Space>
              <Input
                value={credentials.username}
                readOnly
                style={{
                  background: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                  cursor: 'default',
                  width: '220px'
                }}
              />
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copyToClipboard(credentials.username)}
              />
            </Space>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>Password:</Text>
            <Space>
              <Input
                value={credentials.password}
                readOnly
                style={{
                  background: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                  cursor: 'default',
                  width: '220px'
                }}
              />
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copyToClipboard(credentials.password)}
              />
            </Space>
          </div>
        </Space>
      </Modal>
      <Modal
        title="Risultato Eliminazione Credenziali"
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDeleteModalVisible(false)}>
            Chiudi
          </Button>
        ]}
      >
        {deleteResult === "OK" ? (
          <Result
            status="success"
            title="Credenziali eliminate con successo"
          />
        ) : (
          <Result
            status="error"
            title="Errore nell'eliminazione delle credenziali"
            subTitle="Si è verificato un errore durante l'eliminazione delle credenziali."
          />
        )}
      </Modal>
    </Card>
  );
};

const OrgFatturazioneElettronica = ({ orgId }) => {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: companiesQuery,
            variables: { orgId },
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const edges = response.data?.companies?.edges || [];
        setCompanies(edges.map((e) => e.node).filter((c) => c.enabled !== false));
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setLoadingCompanies(false);
      }
    };

    if (orgId) fetchCompanies();
  }, [orgId, getTokenSilently, env]);

  if (loadingCompanies) {
    return <Spin />;
  }

  // Single company or no companies: show simple view (like before)
  if (companies.length <= 1) {
    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <CredentialPanel
              orgId={orgId}
              companyId={companies.length === 1 ? companies[0].objectId : undefined}
              getTokenSilently={getTokenSilently}
              isProd={isProd}
            />
          </Col>
        </Row>
      </div>
    );
  }

  // Multiple companies: show tabs
  const tabItems = [
    {
      key: "_default",
      label: "Organizzazione (default)",
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <CredentialPanel
              orgId={orgId}
              companyId={undefined}
              companyLabel="Credenziali a livello di organizzazione (fallback per le aziende senza credenziali proprie)"
              getTokenSilently={getTokenSilently}
              isProd={isProd}
            />
          </Col>
        </Row>
      ),
    },
    ...companies.map((company) => ({
      key: company.objectId,
      label: (
        <Space>
          <BankOutlined />
          <span>{company.nome || "Azienda"}</span>
          {company.partita_iva && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({company.partita_iva})
            </Text>
          )}
        </Space>
      ),
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <CredentialPanel
              orgId={orgId}
              companyId={company.objectId}
              companyLabel={`${company.nome || "Azienda"}${company.partita_iva ? ` - P.IVA: ${company.partita_iva}` : ""}`}
              getTokenSilently={getTokenSilently}
              isProd={isProd}
            />
          </Col>
        </Row>
      ),
    })),
  ];

  return (
    <div>
      <Tabs items={tabItems} defaultActiveKey="_default" />
    </div>
  );
};

export default OrgFatturazioneElettronica;
